/**
 * Per-day "copy this day to other days" affordance (DNKADM-0088) shared by the
 * three weekly grids (staff schedule, resource schedule, location hours). A small
 * copy `IconAction` on a day row opens a menu listing the other six days as
 * checkboxes (plus an "All other days" shortcut) and an **Apply** button. On
 * Apply it reports the chosen target `dayOfWeek`s to the grid, which deep-copies
 * the source day into them in local state (via `copyDay`) and stages the change
 * for the grid's existing whole-week Save — this component fires NO request.
 *
 * Purely presentational: the copy semantics live in `@/utils/scheduleCopy`; the
 * grid owns the state mutation and the save.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';

interface CopyDayButtonProps {
  /** The source day-of-week (0=Sun … 6=Sat) this button copies FROM. */
  sourceDow: number;
  /** Day labels indexed by `dayOfWeek` (0=Sunday … 6=Saturday). */
  dayLabels: string[];
  /** Disabled while the grid is saving. */
  disabled?: boolean;
  /** Apply the copy to the chosen target days (never includes the source). */
  onApply: (targetDows: number[]) => void;
}

export function CopyDayButton({
  sourceDow,
  dayLabels,
  disabled = false,
  onApply,
}: CopyDayButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<Set<number>>(() => new Set<number>());
  const open = anchorEl !== null;
  const sourceLabel = dayLabels[sourceDow] ?? `Day ${sourceDow}`;
  // The other six days, in `dayOfWeek` order — the copy targets.
  const targetDows = dayLabels.map((_label, dow) => dow).filter((dow) => dow !== sourceDow);
  const allSelected = selected.size === targetDows.length && targetDows.length > 0;
  const someSelected = selected.size > 0;

  function handleOpen(event: React.MouseEvent<HTMLElement>) {
    setSelected(new Set<number>());
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function toggle(dow: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(dow)) next.delete(dow);
      else next.add(dow);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => (prev.size === targetDows.length ? new Set<number>() : new Set(targetDows)));
  }

  function handleApply() {
    onApply([...selected]);
    handleClose();
  }

  return (
    <>
      <IconAction
        size="small"
        label={`Copy ${sourceLabel} to other days`}
        onClick={handleOpen}
        disabled={disabled}
      >
        <Icon name="content_copy" sx={{ fontSize: 18 }} />
      </IconAction>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ list: { dense: true, sx: { minWidth: 220 } } }}
      >
        <Typography sx={{ px: 2, pt: 0.5, pb: 1, fontSize: 12.5, fontWeight: 600 }}>
          {`Copy ${sourceLabel} to`}
        </Typography>
        <MenuItem onClick={toggleAll}>
          <Checkbox
            edge="start"
            size="small"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            tabIndex={-1}
            disableRipple
            slotProps={{ input: { 'aria-label': 'All other days' } }}
          />
          <ListItemText primary="All other days" />
        </MenuItem>
        <Divider />
        {targetDows.map((dow) => (
          <MenuItem key={dow} onClick={() => toggle(dow)}>
            <Checkbox
              edge="start"
              size="small"
              checked={selected.has(dow)}
              tabIndex={-1}
              disableRipple
              slotProps={{ input: { 'aria-label': dayLabels[dow] ?? `Day ${dow}` } }}
            />
            <ListItemText primary={dayLabels[dow] ?? `Day ${dow}`} />
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" variant="contained" onClick={handleApply} disabled={!someSelected}>
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
}
